/*
 * DrawerFilter.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  InputAdornment,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { useEffect, useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SearchIcon from "@mui/icons-material/Search";
import { getConfig } from "../../../config";

const getCountryName = (country) => {
  // If it's an object with name property, use that
  if (typeof country === "object" && country.name) {
    return country.name;
  }
  // If it's a string, return as is (fallback)
  return country;
};

const getEcuName = (ecu) => {
  // If it's an object with name property, use that
  if (typeof ecu === "object" && ecu?.name) {
    return ecu.name;
  }
  // If it's an object with ecuType property, use that
  if (typeof ecu === "object" && ecu?.ecuType) {
    return ecu.ecuType;
  }
  // If it's a string, return as is (fallback)
  return String(ecu);
};

// Define default arrays outside component to prevent re-creation on every render
const DEFAULT_SELECTED_VERSIONS = [];
const DEFAULT_SELECTED_MAKES = [];
const DEFAULT_SELECTED_COUNTRIES = [];
const DEFAULT_SELECTED_ECUS = [];

export const DrawerFilter = ({
  openFilter,
  toggleDrawer,
  selectedVersions = DEFAULT_SELECTED_VERSIONS,
  selectedMakes = DEFAULT_SELECTED_MAKES,
  selectedCountries = DEFAULT_SELECTED_COUNTRIES,
  selectedEcus = DEFAULT_SELECTED_ECUS,
  handleFilterApiCalls,
}) => {
  const [versionOpen, setVersionOpen] = useState(true);
  const [makesOpen, setMakesOpen] = useState(true);
  const [countryOpen, setCountryOpen] = useState(true);
  const [ecuOpen, setEcuOpen] = useState(true);

  const [selectAllVersionFlag, setSelectAllVersionFlag] = useState(false);
  const [selectAllMakeFlag, setSelectAllMakeFlag] = useState(false);
  const [selectAllCountryFlag, setSelectAllCountryFlag] = useState(false);
  const [selectEcuTypeFlag, setSelectEcuTypeFlag] = useState(false);

  const [versionListchecked, setVersionListchecked] = useState([]);
  const [makesListchecked, setMakesListchecked] = useState([]);
  const [countryListchecked, setCountryListchecked] = useState([]);
  const [ecuTypechecked, setEcuTypechecked] = useState([]);

  const [searchVersion, setSearchVersion] = useState("");
  const [searchMakes, setSearchMakes] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [searchEcuType, setSearchEcuType] = useState("");

  const ecuInitializedRef = useRef(false);

  const isCountrySelected = (country, countryListchecked) => {
    return countryListchecked.some((checkedCountry) => {
      // Both are objects, compare by ID
      if (typeof checkedCountry === "object" && typeof country === "object") {
        return checkedCountry.iso3Code === country.iso3Code;
      }
      // Both are strings, direct comparison
      if (typeof checkedCountry === "string" && typeof country === "string") {
        return checkedCountry === country;
      }
      // One is object, one is string - compare by ISO code
      if (typeof checkedCountry === "object" && typeof country === "string") {
        return checkedCountry.iso3Code === country;
      }
      if (typeof checkedCountry === "string" && typeof country === "object") {
        return checkedCountry === country.iso3Code;
      }
      return false;
    });
  };

  const handleCancelFilters = () => {
    // Reset to previously applied/saved values
    if (
      getConfig().REACT_APP_PORTAL !== "oem-admin" &&
      getConfig().REACT_APP_PORTAL !== "domain-approver"
    ) {
      setVersionListchecked(selectedVersions.filter((item) => item !== ""));
      setMakesListchecked(selectedMakes.filter((item) => item !== ""));
    }

    // Reset country list to saved values
    const validCountries = selectedCountries.filter((item) => {
      if (typeof item === "string") {
        return item !== "";
      }
      return item && (item.name || item.iso3Code);
    });
    setCountryListchecked(validCountries);

    // Reset ECU types to saved values
    setEcuTypechecked(selectedEcus.length > 0 ? [...selectedEcus] : []);

    // Reset search states
    setSearchVersion("");
    setSearchMakes("");
    setSearchCountry("");
    setSearchEcuType("");

    // Close the drawer
    toggleDrawer(false)();
  };

  const handleVersionClick = () => {
    setVersionOpen(!versionOpen);
  };

  const handleMakesClick = () => {
    setMakesOpen(!makesOpen);
  };

  const handleCountryClick = () => {
    setCountryOpen(!countryOpen);
  };

  const handleVersionToggle = (value) => () => {
    const currentIndex = versionListchecked.indexOf(value);

    const newChecked = [...versionListchecked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setVersionListchecked(newChecked);
  };

  const handleMakesToggle = (value) => () => {
    const currentIndex = makesListchecked.indexOf(value);

    const newChecked = [...makesListchecked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setMakesListchecked(newChecked);
  };

  const handleCountryToggle = (value) => () => {
    const newChecked = [...countryListchecked];

    // Find if the country is already selected using the same logic as isCountrySelected
    const existingIndex = countryListchecked.findIndex((checkedCountry) => {
      // Both are objects, compare by ID
      if (typeof checkedCountry === "object" && typeof value === "object") {
        return checkedCountry.iso3Code === value.iso3Code;
      }
      // Both are strings, direct comparison
      if (typeof checkedCountry === "string" && typeof value === "string") {
        return checkedCountry === value;
      }
      // One is object, one is string - compare by ISO code
      if (typeof checkedCountry === "object" && typeof value === "string") {
        return checkedCountry.iso3Code === value;
      }
      if (typeof checkedCountry === "string" && typeof value === "object") {
        return checkedCountry === value.iso3Code;
      }
      return false;
    });

    if (existingIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(existingIndex, 1);
    }

    setCountryListchecked(newChecked);
  };

  const handleApplyFilters = () => {
    let data = {
      selectedVersions: versionListchecked,
      selectedMakes: makesListchecked,
      selectedCountries: countryListchecked,
      selectedEcus: ecuTypechecked,
    };
    handleFilterApiCalls(data);
    setSearchVersion("");
    setSearchMakes("");
    setSearchCountry("");
    setSearchEcuType("");
    toggleDrawer(false)();
  };

  const handleClearAllSelection = () => {
    setVersionListchecked([]);
    setMakesListchecked([]);
    setCountryListchecked([]);
    setSelectAllVersionFlag(true);
    setSelectAllMakeFlag(true);
    setSelectAllCountryFlag(true);
    setEcuTypechecked([]);
    setSelectEcuTypeFlag(true);

    setSearchVersion("");
    setSearchMakes("");
    setSearchCountry("");
    setSearchEcuType("");
  };

  useEffect(() => {
    if (openFilter) {
      setSearchVersion("");
      setSearchMakes("");
      setSearchCountry("");
      setSearchEcuType("");
    }
  }, [openFilter]);

  const handleVersionSearch = (event) => {
    setSearchVersion(event.target.value);
  };

  const handleMakesSearch = (event) => {
    setSearchMakes(event.target.value);
  };

  const handleCountrySearch = (event) => {
    setSearchCountry(event.target.value);
  };

  const filteredVersions = selectedVersions.filter((item) =>
    String(item).toLowerCase().includes(searchVersion.toLowerCase())
  );

  const filteredMakes = selectedMakes.filter((item) =>
    String(item).toLowerCase().includes(searchMakes.toLowerCase())
  );

  const handleUnselectVersion = () => {
    setSelectAllVersionFlag(!selectAllVersionFlag);
    if (selectAllVersionFlag) {
      setVersionListchecked(selectedVersions.filter((item) => item !== ""));
    } else {
      setVersionListchecked([]);
    }
  };

  const handleUnselectMakes = () => {
    setSelectAllMakeFlag(!selectAllMakeFlag);
    if (selectAllMakeFlag) {
      setMakesListchecked(selectedMakes.filter((item) => item !== ""));
    } else {
      setMakesListchecked([]);
    }
  };

  const handleUnselectCountry = () => {
    setSelectAllCountryFlag(!selectAllCountryFlag);
    if (selectAllCountryFlag) {
      // Select all countries, filtering out empty items
      const validCountries = selectedCountries.filter((item) => {
        // Handle both string and object types
        if (typeof item === "string") {
          return item !== "";
        }
        // For objects, check if they have valid data
        return item && (item.name || item.iso3Code);
      });
      setCountryListchecked(validCountries);
    } else {
      setCountryListchecked([]);
    }
  };
  const filteredCountry = useMemo(() => {
    return selectedCountries?.filter((country) => {
      const countryText = getCountryName(country);
      return String(countryText)
        .toLowerCase()
        .includes(searchCountry.toLowerCase());
    });
  }, [searchCountry, selectedCountries]);

  // Initialize version and makes lists for non-oem-admin portals
  useEffect(() => {
    if (
      getConfig().REACT_APP_PORTAL !== "oem-admin" &&
      getConfig().REACT_APP_PORTAL !== "domain-approver"
    ) {
      setVersionListchecked(selectedVersions.filter((item) => item !== ""));
    }
  }, [selectedVersions]);

  useEffect(() => {
    if (
      getConfig().REACT_APP_PORTAL !== "oem-admin" &&
      getConfig().REACT_APP_PORTAL !== "domain-approver"
    ) {
      setMakesListchecked(selectedMakes.filter((item) => item !== ""));
    }
  }, [selectedMakes]);

  // Initialize country list
  useEffect(() => {
    const validCountries = selectedCountries.filter((item) => {
      if (typeof item === "string") {
        return item !== "";
      }
      return item && (item.name || item.iso3Code);
    });
    setCountryListchecked(validCountries);
  }, [selectedCountries]);

  const filteredEcu = useMemo(() => {
    return selectedEcus?.filter((item) => {
      const ecuText = getEcuName(item);
      return String(ecuText)
        .toLowerCase()
        .includes(searchEcuType.toLowerCase());
    });
  }, [searchEcuType, selectedEcus]);

  useEffect(() => {
    // Only set ECU types if they haven't been initialized yet and we have selected ECUs
    if (!ecuInitializedRef.current && selectedEcus.length > 0) {
      setEcuTypechecked(selectedEcus);
      ecuInitializedRef.current = true;
    } else if (selectedEcus.length === 0) {
      // Reset if selectedEcus becomes empty
      setEcuTypechecked([]);
      ecuInitializedRef.current = false;
    }
  }, [selectedEcus]);

  // Update version flag based on selection state
  useEffect(() => {
    const validVersions = selectedVersions.filter((item) => item !== "");
    setSelectAllVersionFlag(versionListchecked.length !== validVersions.length);
  }, [versionListchecked, selectedVersions]);

  // Update makes flag based on selection state
  useEffect(() => {
    const validMakes = selectedMakes.filter((item) => item !== "");
    setSelectAllMakeFlag(makesListchecked.length !== validMakes.length);
  }, [makesListchecked, selectedMakes]);

  // Update country flag based on selection state
  useEffect(() => {
    const validCountries = selectedCountries.filter((item) => {
      if (typeof item === "string") {
        return item !== "";
      }
      return item && (item.name || item.iso3Code);
    });

    // If all valid countries are selected, set flag to false (shows "Unselect All")
    // If not all are selected, set flag to true (shows "Select All")
    setSelectAllCountryFlag(
      countryListchecked.length !== validCountries.length
    );
  }, [countryListchecked, selectedCountries]);

  const handleEcuSearch = (e) => {
    setSearchEcuType(e.target.value);
  };

  const isEcuSelected = (ecu, ecuTypechecked) => {
    return ecuTypechecked.some((checkedEcu) => {
      // Both are objects, compare by name or ecuType
      if (typeof checkedEcu === "object" && typeof ecu === "object") {
        return (
          (checkedEcu.name && ecu.name && checkedEcu.name === ecu.name) ||
          (checkedEcu.ecuType &&
            ecu.ecuType &&
            checkedEcu.ecuType === ecu.ecuType) ||
          (checkedEcu.id && ecu.id && checkedEcu.id === ecu.id)
        );
      }
      // Both are strings, direct comparison
      if (typeof checkedEcu === "string" && typeof ecu === "string") {
        return checkedEcu === ecu;
      }
      // One is object, one is string - compare by name or ecuType
      if (typeof checkedEcu === "object" && typeof ecu === "string") {
        return checkedEcu.name === ecu || checkedEcu.ecuType === ecu;
      }
      if (typeof checkedEcu === "string" && typeof ecu === "object") {
        return checkedEcu === ecu.name || checkedEcu === ecu.ecuType;
      }
      return false;
    });
  };

  const handleEcuToggle = (value) => () => {
    const newChecked = [...ecuTypechecked];

    // Find if the ECU is already selected using the same logic as isEcuSelected
    const existingIndex = ecuTypechecked.findIndex((checkedEcu) => {
      // Both are objects, compare by name or ecuType
      if (typeof checkedEcu === "object" && typeof value === "object") {
        return (
          (checkedEcu.name && value.name && checkedEcu.name === value.name) ||
          (checkedEcu.ecuType &&
            value.ecuType &&
            checkedEcu.ecuType === value.ecuType) ||
          (checkedEcu.id && value.id && checkedEcu.id === value.id)
        );
      }
      // Both are strings, direct comparison
      if (typeof checkedEcu === "string" && typeof value === "string") {
        return checkedEcu === value;
      }
      // One is object, one is string - compare by name or ecuType
      if (typeof checkedEcu === "object" && typeof value === "string") {
        return checkedEcu.name === value || checkedEcu.ecuType === value;
      }
      if (typeof checkedEcu === "string" && typeof value === "object") {
        return checkedEcu === value.name || checkedEcu === value.ecuType;
      }
      return false;
    });

    if (existingIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(existingIndex, 1);
    }
    setEcuTypechecked(newChecked);
  };

  const handleSelectUnselectEcu = () => {
    setSelectEcuTypeFlag(!selectEcuTypeFlag);
    if (selectEcuTypeFlag) {
      setEcuTypechecked([...selectedEcus]);
    } else {
      setEcuTypechecked([]);
    }
  };

  return (
    <Drawer open={openFilter} onClose={toggleDrawer(false)} anchor="right">
      <div
        className="filterContainer"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <div className="filterHeaderCls">
          <span>Filter</span>
          <span>
            <CloseIcon className="closeIconCls" onClick={toggleDrawer(false)} />
          </span>
        </div>
        <div
          className="filterContentCls"
          style={{
            flex: 1,
            overflowY: "auto",
            paddingBottom: "16px",
          }}
        >
          {getConfig().REACT_APP_PORTAL !== "oem-admin" &&
            getConfig().REACT_APP_PORTAL !== "domain-approver" && (
              <List
                sx={{
                  width: "100%",
                  // maxWidth: 383,
                  maxWidth: 350,
                  bgcolor: "background.paper",
                }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                id="version"
              >
                <ListItemButton
                  onClick={handleVersionClick}
                  sx={{ fontSize: "14px", fontWeight: "500", color: "#121415" }}
                >
                  {versionOpen ? (
                    <ArrowDropUpIcon style={{ color: "#414344 !important" }} />
                  ) : (
                    <ArrowDropDownIcon
                      style={{ color: "#717273 !important" }}
                    />
                  )}

                  <ListItemText
                    primary="Version"
                    disableTypography
                    sx={{
                      fontSize: "16px !important",
                      fontWeight: "600 !important",
                      color: "#121415",
                    }}
                  />
                  {versionListchecked.length}
                </ListItemButton>

                <Collapse in={versionOpen} timeout="auto" unmountOnExit>
                  <div className="versionListDivCls">
                    <Box
                      className="searchCreateBtnCls"
                      component="form"
                      noValidate
                      autoComplete="off"
                    >
                      {!selectAllVersionFlag && (
                        <button
                          className="selectAllTextCls"
                          onClick={handleUnselectVersion}
                          type="button"
                        >
                          Unselect All
                        </button>
                      )}
                      {selectAllVersionFlag && (
                        <button
                          className="selectAllTextCls"
                          onClick={handleUnselectVersion}
                          type="button"
                        >
                          Select All
                        </button>
                      )}

                      <TextField
                        type="search"
                        placeholder="Search for versions"
                        size="small"
                        style={{
                          backgroundColor: "white",
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        onChange={handleVersionSearch}
                      />
                    </Box>
                  </div>
                  <div className="listDivCls">
                    {filteredVersions
                      .filter((version) => version !== "")
                      .map((version) => (
                        <List
                          key={version}
                          component="div"
                          sx={{
                            paddingTop: "2px!important",
                            paddingBottom: "2px!important",
                          }}
                        >
                          <ListItemButton
                            sx={{
                              minWidth: "0px !important",
                              borderRadius: "4px!important",
                              paddingTop: "0px!important",
                              paddingBottom: "0px!important",
                              backgroundColor: versionListchecked.includes(
                                version
                              )
                                ? "#E5F3FF"
                                : "",
                              color: versionListchecked.includes(version)
                                ? "#0476E2"
                                : "",
                              cursor: "default",
                            }}
                            onClick={handleVersionToggle(version)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={versionListchecked.includes(version)}
                                tabIndex={-1}
                                disableRipple
                                slotProps={{ "aria-labelledby": version }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              disableTypography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                marginLeft: "-32px",
                              }}
                              primary={version}
                            />
                          </ListItemButton>
                        </List>
                      ))}
                  </div>
                </Collapse>
              </List>
            )}
          {getConfig().REACT_APP_PORTAL !== "oem-admin" &&
            getConfig().REACT_APP_PORTAL !== "domain-approver" && (
              <List
                sx={{
                  width: "100%",
                  maxWidth: 350,
                  bgcolor: "background.paper",
                }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                id="makes"
              >
                <ListItemButton
                  onClick={handleMakesClick}
                  sx={{ fontSize: "14px", fontWeight: "500", color: "#121415" }}
                >
                  {makesOpen ? (
                    <ArrowDropUpIcon style={{ color: "#414344 !important" }} />
                  ) : (
                    <ArrowDropDownIcon
                      style={{ color: "#717273 !important" }}
                    />
                  )}

                  <ListItemText
                    primary="Vehicle Make"
                    disableTypography
                    sx={{
                      fontSize: "16px !important",
                      fontWeight: "600 !important",
                      color: "#121415",
                    }}
                  />
                  {makesListchecked.length}
                </ListItemButton>

                <Collapse in={makesOpen} timeout="auto" unmountOnExit>
                  <div className="versionListDivCls">
                    <Box
                      className="searchCreateBtnCls"
                      component="form"
                      noValidate
                      autoComplete="off"
                    >
                      {!selectAllMakeFlag && (
                        <button
                          className="selectAllTextCls"
                          onClick={handleUnselectMakes}
                          type="button"
                        >
                          Unselect All
                        </button>
                      )}
                      {selectAllMakeFlag && (
                        <button
                          className="selectAllTextCls"
                          onClick={handleUnselectMakes}
                          type="button"
                        >
                          Select All
                        </button>
                      )}

                      <TextField
                        type="search"
                        placeholder="Search for Makes"
                        size="small"
                        style={{
                          backgroundColor: "white",
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        onChange={handleMakesSearch}
                      />
                    </Box>
                  </div>
                  <div className="listDivCls">
                    {filteredMakes
                      .filter((make) => make !== "")
                      .map((make) => (
                        <List
                          key={make}
                          component="div"
                          sx={{
                            paddingTop: "2px!important",
                            paddingBottom: "2px!important",
                          }}
                        >
                          <ListItemButton
                            sx={{
                              minWidth: "0px !important",
                              borderRadius: "4px!important",
                              paddingTop: "0px!important",
                              paddingBottom: "0px!important",
                              backgroundColor: makesListchecked.includes(make)
                                ? "#E5F3FF"
                                : "",
                              color: makesListchecked.includes(make)
                                ? "#0476E2"
                                : "",
                              cursor: "default",
                            }}
                            onClick={handleMakesToggle(make)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={makesListchecked.includes(make)}
                                tabIndex={-1}
                                disableRipple
                                slotProps={{ "aria-labelledby": make }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              disableTypography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                marginLeft: "-32px",
                              }}
                              primary={make}
                            />
                          </ListItemButton>
                        </List>
                      ))}
                  </div>
                </Collapse>
              </List>
            )}
          {(getConfig().REACT_APP_PORTAL === "oem-admin" ||
            getConfig().REACT_APP_PORTAL === "domain-approver") && (
            <List
              sx={{ width: "100%", maxWidth: 350, bgcolor: "background.paper" }}
              component="nav"
              aria-labelledby="nested-list-subheader"
              id="ecuType"
            >
              <ListItemButton
                onClick={() => setEcuOpen(!ecuOpen)}
                sx={{ fontSize: "14px", fontWeight: "500", color: "#121415" }}
              >
                {ecuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                <ListItemText
                  primary="ECU Types"
                  disableTypography
                  sx={{
                    fontSize: "16px !important",
                    fontWeight: "600 !important",
                    color: "#121415",
                  }}
                />
                {ecuTypechecked.length}/{selectedEcus.length}
              </ListItemButton>
              <Collapse in={ecuOpen} timeout="auto" unmountOnExit>
                <div className="versionListDivCls">
                  <Box
                    className="searchCreateBtnCls"
                    component="form"
                    noValidate
                    autoComplete="off"
                  >
                    <button
                      className="selectAllTextCls"
                      onClick={handleSelectUnselectEcu}
                      type="button"
                    >
                      {selectEcuTypeFlag ? "Select All" : "Unselect All"}
                    </button>
                    <TextField
                      type="search"
                      placeholder="Search for ECU Types"
                      size="small"
                      style={{ backgroundColor: "white" }}
                      onChange={handleEcuSearch}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </div>
                <div className="listDivCls">
                  {filteredEcu.map((ecu, index) => (
                    <List
                      key={
                        typeof ecu === "object"
                          ? ecu.name || ecu.ecuType || ecu.id || index
                          : ecu
                      }
                      component="div"
                      sx={{ paddingTop: "2px", paddingBottom: "2px" }}
                    >
                      <ListItemButton
                        sx={{
                          minWidth: "0px",
                          borderRadius: "4px",
                          paddingTop: "0px",
                          paddingBottom: "0px",
                          backgroundColor: isEcuSelected(ecu, ecuTypechecked)
                            ? "#E5F3FF"
                            : "",
                          color: isEcuSelected(ecu, ecuTypechecked)
                            ? "#0476E2"
                            : "",
                          cursor: "default",
                        }}
                        onClick={handleEcuToggle(ecu)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isEcuSelected(ecu, ecuTypechecked)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginLeft: "-32px",
                          }}
                          primary={getEcuName(ecu)}
                        />
                      </ListItemButton>
                    </List>
                  ))}
                </div>
              </Collapse>
            </List>
          )}
          <List
            sx={{ width: "100%", maxWidth: 350, bgcolor: "background.paper" }}
            component="nav"
            aria-labelledby="nested-list-subheader"
            id="country"
          >
            <ListItemButton
              onClick={handleCountryClick}
              sx={{ fontSize: "14px", fontWeight: "500", color: "#121415" }}
            >
              {countryOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              <ListItemText
                primary="Country"
                disableTypography
                sx={{
                  fontSize: "16px !important",
                  fontWeight: "600 !important",
                  color: "#121415",
                }}
              />
              {countryListchecked.length}/
              {
                selectedCountries.filter((country) =>
                  typeof country === "string"
                    ? country !== ""
                    : country && (country.name || country.iso3Code)
                ).length
              }
            </ListItemButton>
            <Collapse in={countryOpen} timeout="auto" unmountOnExit>
              <div className="versionListDivCls">
                <Box
                  className="searchCreateBtnCls"
                  component="form"
                  noValidate
                  autoComplete="off"
                >
                  {!selectAllCountryFlag && (
                    <button
                      className="selectAllTextCls"
                      onClick={handleUnselectCountry}
                      type="button"
                    >
                      Unselect All
                    </button>
                  )}
                  {selectAllCountryFlag && (
                    <button
                      className="selectAllTextCls"
                      onClick={handleUnselectCountry}
                      type="button"
                    >
                      Select All
                    </button>
                  )}

                  <TextField
                    type="search"
                    placeholder="Search for Country"
                    size="small"
                    style={{
                      backgroundColor: "white",
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                    onChange={handleCountrySearch}
                  />
                </Box>
              </div>
              <div className="listDivCls">
                {filteredCountry
                  .filter((country) => country !== "")
                  .map((country, index) => (
                    <List
                      key={
                        typeof country === "object"
                          ? country.id || country.iso3Code || index
                          : country
                      }
                      component="div"
                      sx={{
                        paddingTop: "2px!important",
                        paddingBottom: "2px!important",
                      }}
                    >
                      <ListItemButton
                        sx={{
                          minWidth: "0px !important",
                          borderRadius: "4px!important",
                          paddingTop: "0px!important",
                          paddingBottom: "0px!important",
                          backgroundColor: isCountrySelected(
                            country,
                            countryListchecked
                          )
                            ? "#E5F3FF"
                            : "",
                          color: isCountrySelected(country, countryListchecked)
                            ? "#0476E2"
                            : "",
                          cursor: "default",
                        }}
                        onClick={handleCountryToggle(country)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isCountrySelected(
                              country,
                              countryListchecked
                            )}
                            tabIndex={-1}
                            disableRipple
                            slotProps={{ "aria-labelledby": country }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginLeft: "-32px",
                          }}
                          primary={getCountryName(country)}
                        />
                      </ListItemButton>
                    </List>
                  ))}
              </div>
            </Collapse>
          </List>
        </div>
        <div
          className="filterFooterCls"
          style={{
            position: "relative",
            marginTop: "auto",
            flexShrink: 0,
          }}
        >
          <button
            className="clearAllCls"
            onClick={handleClearAllSelection}
            style={{fontWeight: 'bold', background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
          >
            Reset to Default
          </button>
          <div>
            <Button
              onClick={handleCancelFilters}
              color="primary"
              sx={{
                textTransform: "none",
                background: "#1214151A",
                color: "#2A2C2D",
              }}
            >
              Cancel
            </Button>
            &nbsp;&nbsp;
            <Button
              color="primary"
              variant="contained"
              sx={{ textTransform: "none" }}
              onClick={handleApplyFilters}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

DrawerFilter.propTypes = {
  openFilter: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  selectedVersions: PropTypes.array,
  selectedMakes: PropTypes.array,
  selectedCountries: PropTypes.array,
  selectedEcus: PropTypes.array,
  handleFilterApiCalls: PropTypes.func.isRequired,
};
